import { createContext, useContext, useState, useMemo, useCallback, ReactNode } from 'react';

export interface MonthSelection {
  year: number;
  month: number;
}

interface MonthContextType {
  selectedMonth: MonthSelection;
  setSelectedMonth: (month: MonthSelection) => void;
  isAllTime: boolean;
  setIsAllTime: (value: boolean) => void;
  selectMonth: (month: MonthSelection) => void;
}

const MonthContext = createContext<MonthContextType | undefined>(undefined);

export function MonthProvider({ children }: { children: ReactNode }) {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState<MonthSelection>({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });
  const [isAllTime, setIsAllTime] = useState(false);

  const selectMonth = useCallback((month: MonthSelection) => {
    setSelectedMonth(month);
    setIsAllTime(false);
  }, []);

  const value = useMemo(() => ({
    selectedMonth, setSelectedMonth, isAllTime, setIsAllTime, selectMonth,
  }), [selectedMonth, isAllTime, selectMonth]);

  return (
    <MonthContext.Provider value={value}>
      {children}
    </MonthContext.Provider>
  );
}

export function useMonth() {
  const context = useContext(MonthContext);
  if (context === undefined) {
    throw new Error('useMonth must be used within a MonthProvider');
  }
  return context;
}
